import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  formatNumber,
  tooltipContentStyle,
  tooltipCursorStyle,
} from "utils/dashboardUtils";

const RevenueStreamChart = () => {
  const { t } = useTranslation();

  const revenueData = useSelector(
    (state) => state.Dashboard?.data?.charts?.revenueChart,
  );

  // Calculate max stacked value
  const maxStack = useMemo(() => {
    if (!revenueData?.length) return 0;
    return Math.max(
      ...revenueData.map(
        (d) => (d.membership || 0) + (d.oneTime || 0) + (d.addons || 0),
      ),
    );
  }, [revenueData]);

  const COLORS = {
    membership: "#53c3eb",
    oneTime: "#de235c",
    addons: "#523677",
  };

  return (
    <div className="revenue_chart dashboard_common_card">
      {/* Header */}
      <div className="revenue_chart__header mb-4">
        <div>
          <h4 className="fs_20">{t("Revenue Stream Analysis")}</h4>
          <p className="page_sub_title">{t("Last 6 months Breakdown")}</p>
        </div>

        <div className="revenue_chart__legend">
          <span className="legend membership">{t("Membership")}</span>
          <span className="legend oneTime">{t("One Time Wash")}</span>
          <span className="legend addons">{t("Add-ons")}</span>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={340}>
        <BarChart
          data={revenueData}
          barCategoryGap="25%"
          margin={{
            top: 20,
            right: 10,
            left: 10,
            bottom: 10,
          }}
        >
          <CartesianGrid strokeWidth={0.4} vertical={false} stroke="#E5E7EB" />
          <Tooltip
            formatter={(value, name) => {
              const labels = {
                membership: t("Membership"),
                oneTime: t("One Time Wash"),
                addons: t("Add-ons"),
              };

              return [
                `SAR ${Number(value ?? 0).toLocaleString()}`,
                labels[name],
              ];
            }}
            cursor={tooltipCursorStyle}
            contentStyle={tooltipContentStyle}
          />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#6B7280", fontSize: 14, fontWeight: 500 }}
            tickMargin={12}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            width={80}
            tick={{ fill: "#374151", fontSize: 13 }}
            tickFormatter={(value) => {
              if (value >= 1_000_000)
                return `SAR ${(value / 1_000_000).toFixed(1)}M`;
              if (value >= 1_000) return `SAR ${(value / 1_000).toFixed(0)}K`;
              return `SAR ${value}`;
            }}
            domain={[
              0,
              (dataMax) => {
                const padded = dataMax * 1.15;
                const magnitude = Math.pow(10, Math.floor(Math.log10(padded)));
                return Math.ceil(padded / magnitude) * magnitude; // rounds up to clean tick
              },
            ]}
          />
          <Bar
            dataKey="membership"
            stackId="revenue"
            fill={COLORS.membership}
            radius={[4, 4, 0, 0]}
          />
          <Bar dataKey="oneTime" stackId="revenue" fill={COLORS.oneTime} />
          <Bar
            dataKey="addons"
            stackId="revenue"
            fill={COLORS.addons}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RevenueStreamChart;
